import React, { useState, useEffect, lazy, Suspense } from 'react';
import { NavLink } from 'react-router-dom';
import '../styles/Home.scss';
import ContactModal from './ContactModal';
import FadeInSection from '../components/FadeInSection';
import Marquee from './Marquee';
import { motion, useReducedMotion } from 'framer-motion';
import image from '../assets/RGB_LilyPhoto.webp';
import SimpleAnimatedTestimonials from './SimpleAnimatedTestimonials';
import { neighborhoods } from '../constants/neighborhoods';
import { sizedImage } from '../utils/imageUrl';
import CountUp from 'react-countup';
import { Helmet } from 'react-helmet-async';
// Leaflet is ~150 KB and the district map sits well below the fold, but importing it here
// put it in the main bundle on the site's most-visited page. Lazy so it loads on demand.
const SFDistrictMap = lazy(() => import('./SFDistrictMap'));

const statsContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.15, delayChildren: 0.1 } },
};
const statItem = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};

/**
 * Counts up on scroll, but renders the final figure outright for reduced-motion users.
 *
 * That branch is also what the prerenderer takes (it emulates prefers-reduced-motion), which
 * makes the static HTML deterministic. Relying on the scroll-spy animation instead meant the
 * snapshot could catch a counter mid-flight and bake wrong numbers into the page that crawlers
 * and AI engines read — "$71M+ sold, 95+ clients" on one build, "$0M+" on another.
 */
function Stat({ end, prefix = '', suffix = '' }) {
  const reduced = useReducedMotion();
  if (reduced) return <span>{`${prefix}${end}${suffix}`}</span>;
  return <CountUp enableScrollSpy start={0} end={end} prefix={prefix} suffix={suffix} duration={2} />;
}

const FEATURED_SLUGS = ['pacific-heights', 'noe-valley', 'marina', 'san-mateo'];
const featuredNeighborhoods = FEATURED_SLUGS.map(slug => neighborhoods.find(n => n.slug === slug)).filter(Boolean);


function Home() {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    document.body.classList.add('snap-home');
    return () => document.body.classList.remove('snap-home');
  }, []);

  return (
    <>
      <Helmet>
        <title>Lily Lai Real Estate | Bay Area Realtor</title>
        <meta name="description" content="Lily Lai is a top Bay Area Realtor specializing in buying and selling homes in San Francisco, South San Francisco, Pacifica, and the Peninsula. Cantonese and Mandarin speaking. 粤语及普通话服务。" />
        <meta name="keywords" content="旧金山房地产, 湾区房产经纪, 买房, 卖房, 粤语房产经纪, 普通话房产经纪, San Francisco realtor, Bay Area real estate, Cantonese speaking realtor, Mandarin speaking realtor" />
        <link rel='canonical' href='https://www.lilylaisf.com/' />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.lilylaisf.com/" />
        <meta property="og:title" content="Lily Lai Real Estate | Bay Area Realtor" />
        <meta property="og:description" content="Thoughtful, modern real estate in the Bay Area. Buying or selling — guided with care, clarity, and local expertise." />
        <meta property="og:image" content="https://res.cloudinary.com/dlaituqwq/image/upload/f_auto,q_auto/wr8plprsi0qxxs3lhmxr" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Lily Lai Real Estate | Bay Area Realtor" />
        <meta name="twitter:description" content="Thoughtful, modern real estate in the Bay Area. Buying or selling — guided with care, clarity, and local expertise." />
        <meta name="twitter:image" content="https://res.cloudinary.com/dlaituqwq/image/upload/f_auto,q_auto/wr8plprsi0qxxs3lhmxr" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "RealEstateAgent",
            "name": "Lily Lai Real Estate",
            "url": "https://www.lilylaisf.com",
            "telephone": "+14157282623",
            "email": "lily@lilylaisf.com",
            "image": "https://res.cloudinary.com/dlaituqwq/image/upload/f_auto,q_auto/wr8plprsi0qxxs3lhmxr",
            "description": "Top Bay Area Realtor specializing in buying and selling homes in San Francisco, South San Francisco, Pacifica, and the Peninsula. $100M+ in sales, 100+ happy clients, top 5% of SF agents.",
            "priceRange": "$$",
            "areaServed": [
              { "@type": "City", "name": "San Francisco", "sameAs": "https://en.wikipedia.org/wiki/San_Francisco" },
              { "@type": "City", "name": "South San Francisco" },
              { "@type": "City", "name": "Pacifica" },
              { "@type": "AdministrativeArea", "name": "Peninsula, San Mateo County" }
            ],
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "San Francisco",
              "addressRegion": "CA",
              "addressCountry": "US"
            },
            "employee": {
              "@type": "Person",
              "name": "Lily Lai",
              "jobTitle": "Licensed Real Estate Agent",
              "telephone": "+14157282623",
              "email": "lily@lilylaisf.com",
              "image": "https://res.cloudinary.com/dlaituqwq/image/upload/f_auto,q_auto/wr8plprsi0qxxs3lhmxr",
              "knowsLanguage": ["English", "Cantonese", "Mandarin", "粤语", "普通话"],
              "knowsAbout": ["San Francisco real estate", "home buying", "home selling", "home renovation", "Bay Area housing market", "旧金山房地产", "湾区买房", "湾区卖房"],
              "worksFor": {
                "@type": "Organization",
                "name": "The Lurie Group at City Real Estate",
                "url": "https://cityrealestatesf.com/"
              }
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "5",
              "reviewCount": "38",
              "bestRating": "5"
            },
            "sameAs": [
              "https://www.linkedin.com/in/lily-lai-03382a87/",
              "https://luriegroup.com/agent/lily-lai",
              "https://cityrealestatesf.com/",
              "https://share.google/1tH7mPayVavdCtOYg"
            ]
          }
        `}</script>
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How do I buy a home in San Francisco?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Buying a home in San Francisco starts with getting pre-approved for a mortgage, understanding the local market, and working with a knowledgeable local agent. Lily Lai guides buyers through every step — from search and offer strategy to closing — with a focus on making the process clear and stress-free."
                }
              },
              {
                "@type": "Question",
                "name": "How do I sell my home in the Bay Area?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Selling in the Bay Area involves pricing strategy, staging, marketing, and negotiation. Lily Lai creates personalized selling strategies based on your home's unique strengths and current market conditions to help you achieve the best possible outcome."
                }
              },
              {
                "@type": "Question",
                "name": "What is my home worth in San Francisco?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Home values in San Francisco vary significantly by neighborhood, property type, and condition. Lily Lai offers a free AI-powered home valuation tool at https://www.lilylaisf.com/home-valuation that provides an instant estimate based on comparable sales in your area."
                }
              },
              {
                "@type": "Question",
                "name": "Who is the best realtor in San Francisco?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Lily Lai is a top-ranked Bay Area Realtor in the top 5% of San Francisco agents, with over $100 million in sales and 100+ satisfied clients. She specializes in residential real estate across San Francisco, South San Francisco, Pacifica, and the Peninsula."
                }
              },
              {
                "@type": "Question",
                "name": "What areas does Lily Lai serve?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Lily Lai serves San Francisco, South San Francisco, Pacifica, and the broader Peninsula (San Mateo County). She has deep knowledge of local neighborhoods, pricing trends, and market dynamics across all these areas."
                }
              },
              {
                "@type": "Question",
                "name": "Does Lily Lai speak Cantonese or Mandarin?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes. Lily Lai is a Cantonese and Mandarin speaking Bay Area Realtor. She provides full real estate services in Cantonese (粤语) and Mandarin (普通话) for Chinese-speaking buyers and sellers across San Francisco, South San Francisco, Pacifica, and the Peninsula."
                }
              },
              {
                "@type": "Question",
                "name": "旧金山有粤语普通话房产经纪吗？",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "有。黎莉莉（Lily Lai）是湾区专业房产经纪，提供粤语及普通话全程服务，涵盖旧金山、南旧金山、太平洋市及半岛地区的买房与卖房业务。电话：415-728-2623，邮箱：lily@lilylaisf.com。"
                }
              },
              {
                "@type": "Question",
                "name": "如何在旧金山买房？",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "在旧金山买房，首先需要获得贷款预批，了解本地市场行情，并与熟悉湾区的专业经纪合作。Lily Lai 提供粤语及普通话服务，全程协助您从看房、出价到过户，让买房过程清晰、顺畅。"
                }
              }
            ]
          }
        `}</script>
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Lily Lai Real Estate",
            "url": "https://www.lilylaisf.com",
            "logo": "https://www.lilylaisf.com/Round_Oatmeal_SocialMedia.png",
            "telephone": "+14157282623",
            "email": "lily@lilylaisf.com",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "San Francisco",
              "addressRegion": "CA",
              "addressCountry": "US"
            },
            "sameAs": [
              "https://www.instagram.com/lily_lai__/",
              "https://www.facebook.com/lily.lai.7370",
              "https://www.linkedin.com/in/lily-lai-03382a87/",
              "https://www2.dre.ca.gov/PublicASP/pplinfo.asp?License_id=02123349",
              "https://www.homes.com/real-estate-agents/lily-lai/y38mj8v/",
              "https://luriegroup.com/agent/lily-lai",
              "https://cityrealestatesf.com/",
              "https://share.google/1tH7mPayVavdCtOYg"
            ],
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": "+14157282623",
              "email": "lily@lilylaisf.com",
              "contactType": "customer service",
              "areaServed": "US",
              "availableLanguage": ["English", "Cantonese", "Mandarin"]
            },
            "founder": {
              "@type": "Person",
              "name": "Lily Lai",
              "jobTitle": "Licensed Real Estate Agent",
              "knowsLanguage": ["English", "Cantonese", "Mandarin"]
            }
          }
        `}</script>
      </Helmet>
      <div className='home'>

        {/* Crawler-readable stats — visually hidden, readable by AI/search bots */}
        <div className='sr-only'>
          <h1>San Francisco &amp; Peninsula Real Estate — Lily Lai, Bay Area Realtor</h1>
          <p>Lily Lai Real Estate — Bay Area Realtor. $100M+ sold to date. 100+ happy clients. Top 5% of San Francisco agents. Cantonese and Mandarin speaking realtor in San Francisco. 粤语及普通话房产经纪，服务旧金山湾区。</p>
        </div>

        {/* Stats bar */}
        <motion.div
          className='stats'
          variants={statsContainer}
          initial='hidden'
          whileInView='visible'
          viewport={{ once: true, margin: '-40px' }}
        >
          <motion.div className='stat' variants={statItem}>
            <Stat end={100} prefix="$" suffix="M+" />
            <p>Sold To Date</p>
          </motion.div>
          <motion.div className='stat-divider' variants={statItem} />
          <motion.div className='stat' variants={statItem}>
            <Stat end={100} suffix="+" />
            <p>Happy Clients</p>
          </motion.div>
          <motion.div className='stat-divider' variants={statItem} />
          <motion.div className='stat' variants={statItem}>
            <Stat end={5} prefix="Top " suffix="%" />
            <p>Of SF Agents</p>
          </motion.div>
        </motion.div>

        {/* Hero CTA */}
        <div className='cta'>
          <div className='background' />
          <div className='text'>
            <p className='text-eyebrow'>Bay Area Real Estate</p>
            <h2>I'm Curious To<br/>Hear Your Story</h2>
            <p className='subtext'>
              Let's connect and explore how I can help you take the next step.
            </p>
            <ContactModal isOpen={isOpen} setIsOpen={setIsOpen} />
          </div>
        </div>

        <Marquee />

        {/* About */}
        <div className='about'>
          <div className='about-grid'>
            <div className='about-col-image'>
              <FadeInSection>
                <div className='about-portrait-wrapper'>
                  <img loading='eager' src={image} className='about-image' alt='Lily Lai, Bay Area Realtor' />
                </div>
              </FadeInSection>
            </div>
            <div className='about-col-text'>
              <FadeInSection>
                <div className='about-text'>
                  <p className='about-eyebrow'>Bay Area Realtor</p>
                  <h2 className='about-heading'>About Lily</h2>
                  <p className='about-body'>
                    A San Francisco native, Lily has built her reputation on something simpler than credentials: she genuinely cares. Known for turning an overwhelming process into a manageable one, she's the agent clients describe as patient, proactive, and always a step ahead. Her background in renovation gives buyers an eye for true value beyond the surface and sellers a real edge in positioning their home. From first-time buyers to difficult markets, she finds a way to make it work.
                  </p>
                  <p className='about-languages'>
                    <span>Languages</span> English · 粤語 (Cantonese) · 普通話 (Mandarin)
                  </p>
                  <NavLink className='about-link link-underline' to="/about">More About Me →</NavLink>
                </div>
              </FadeInSection>
            </div>
          </div>
        </div>

        {/* SF District Map */}
        <div className='sf-map-section'>
          <FadeInSection>
            <p className='sf-map-section-eyebrow'>San Francisco Market Data</p>
            <h2 className='sf-map-section-title'>Explore by District</h2>
            <p className='sf-map-section-sub'>Hover over any district to see median home prices. Click a highlighted neighborhood to read the full guide.</p>
            <Suspense fallback={<div style={{ height: '580px' }} />}>
              <SFDistrictMap />
            </Suspense>
          </FadeInSection>
        </div>

        {/* Featured Neighborhoods */}
        <div className='featured-neighborhoods'>
          <FadeInSection>
            <p className='featured-neighborhoods-eyebrow'>Explore</p>
            <h2 className='featured-neighborhoods-title'>Neighborhood Guides</h2>
            <p className='featured-neighborhoods-sub'>In-depth guides to market data, local life, and what makes each neighborhood unique.</p>
          </FadeInSection>
          <FadeInSection>
            <div className='featured-neighborhoods-grid'>
              {featuredNeighborhoods.map(n => (
                <NavLink key={n.slug} to={`/neighborhoods/${n.slug}`} className='neighborhood-card' onClick={() => { if (typeof window.gtag === 'function') window.gtag('event', 'home_neighborhood_card_click', { neighborhood_name: n.name }); }}>
                  <div className='neighborhood-card-image'>
                    {n.image
                      ? <img src={sizedImage(n.image, 600)} alt={n.name} loading='lazy' />
                      : <div className='neighborhood-card-placeholder' />
                    }
                    <div className='neighborhood-card-overlay'>
                      <span className='neighborhood-card-region'>
                        {n.region === 'sf' ? 'San Francisco' : 'Peninsula'}
                      </span>
                      <div className='neighborhood-card-info'>
                        <h3 className='neighborhood-card-name'>{n.name}</h3>
                        <p className='neighborhood-card-tagline'>{n.tagline}</p>
                      </div>
                    </div>
                  </div>
                </NavLink>
              ))}
            </div>
            <div className='featured-neighborhoods-cta'>
              <NavLink to='/neighborhoods' className='featured-neighborhoods-link' onClick={() => { if (typeof window.gtag === 'function') window.gtag('event', 'home_view_all_neighborhoods_click'); }}>View All Neighborhood Guides →</NavLink>
            </div>
          </FadeInSection>
        </div>

        {/* Home Valuation CTA */}
        <FadeInSection>
          <div className='valuation-cta'>
            <div className='valuation-cta-content'>
              <p className='valuation-eyebrow'>Free Instant Estimate</p>
              <h2>What Is Your Home Worth?</h2>
              <p>Get an AI-powered valuation with comparable sales in your neighborhood — no commitment required.</p>
              <NavLink className='valuation-btn' to="/home-valuation" onClick={() => { if (typeof window.gtag === 'function') window.gtag('event', 'home_valuation_cta_click'); }}>Get My Home Value →</NavLink>
            </div>
          </div>
        </FadeInSection>

        {/* Resources */}
        <FadeInSection>
          <div className='resources'>
            <p className='resources-eyebrow'>Resources</p>
            <h2 className='resources-title'>Buyer &amp; Seller Guides</h2>
            <div className='resources-grid'>
              <NavLink to='/buyers' className='resource-card' onClick={() => { if (typeof window.gtag === 'function') window.gtag('event', 'resource_card_click', { resource_name: 'buyers_guide' }); }}>
                <p className='resource-card-label'>Step-by-Step Guide</p>
                <h3 className='resource-card-title'>Bay Area Buyer's Guide</h3>
                <p className='resource-card-desc'>From pre-approval to closing day — everything you need to know about buying in the Bay Area.</p>
                <span className='resource-card-link'>Read the Guide →</span>
              </NavLink>
              <NavLink to='/sellers' className='resource-card' onClick={() => { if (typeof window.gtag === 'function') window.gtag('event', 'resource_card_click', { resource_name: 'sellers_guide' }); }}>
                <p className='resource-card-label'>Step-by-Step Guide</p>
                <h3 className='resource-card-title'>Bay Area Seller's Guide</h3>
                <p className='resource-card-desc'>From pricing strategy to closing day — a clear roadmap for selling your Bay Area home.</p>
                <span className='resource-card-link'>Read the Guide →</span>
              </NavLink>
              <NavLink to='/mortgage-calculator' className='resource-card' onClick={() => { if (typeof window.gtag === 'function') window.gtag('event', 'resource_card_click', { resource_name: 'mortgage_calculator' }); }}>
                <p className='resource-card-label'>Buyer Tools</p>
                <h3 className='resource-card-title'>Mortgage Calculator</h3>
                <p className='resource-card-desc'>Estimate your monthly payment with Bay Area county property tax rates built in.</p>
                <span className='resource-card-link'>Open Calculator →</span>
              </NavLink>
            </div>
          </div>
        </FadeInSection>

        {/* Testimonials */}
        <div className='testimonials'>
          <FadeInSection>
            <div className='title'>What Clients Are Saying</div>
          </FadeInSection>
          <SimpleAnimatedTestimonials />
          <FadeInSection>
            <NavLink className='testimonials-link link-underline' to="/testimonials" onClick={() => { if (typeof window.gtag === 'function') window.gtag('event', 'home_read_all_reviews_click'); }}>Read All Reviews →</NavLink>
          </FadeInSection>
        </div>

      </div>
    </>
  );
}

export default Home;
